import type { AssetExtended, AssetStatus } from '@/modules/admin/resources'

// Asset statuses eligible for internal sale
const ELIGIBLE_STATUSES: AssetStatus[] = ['USE', 'RETURN', 'AVAILABLE', 'RETURNED']

// Asset statuses not eligible for internal sale
const INELIGIBLE_STATUSES: AssetStatus[] = [
  'REPAIR',
  'DISCARDED',
  'SOLD',
  'STOLEN',
  'LOST',
  'DELIVERY',
  'CANCELLED',
]

// The only way to check the ownership type
const OWNED_TYPE_ID = -1

/**
 * Checks if an asset is eligible for internal sale
 * Asset must be:
 * 1. Owned (not leased)
 * 2. Have a status in ELIGIBLE_STATUSES
 */
export const isAssetEligibleForSale = (asset: AssetExtended): boolean => {
  // Must be owned
  if (asset.ownershipTypeId !== OWNED_TYPE_ID) {
    return false
  }

  // Must have an eligible status
  const isStatusEligible = ELIGIBLE_STATUSES.includes(asset.status)

  return isStatusEligible
}

/**
 * Checks if an asset is assigned to a specific employee
 * Assets with status 'USE' or 'RETURN' are assigned to an employee
 */
export const isAssetAssignedToEmployee = (asset: AssetExtended): boolean =>
  asset.status === 'USE' || asset.status === 'RETURN'

/**
 * Checks if an asset is available (not assigned to specific employee)
 * Assets with status 'AVAILABLE' or 'RETURNED' are not assigned
 */
export const isAssetAvailable = (asset: AssetExtended): boolean =>
  asset.status === 'AVAILABLE' || asset.status === 'RETURNED'

/**
 * Gets the list of eligible employees for selling an asset
 * - If asset is assigned (USE/FOR_RETURN): only the current employee
 * - If asset is available (AVAILABLE/RETURNED): all employees in the company
 */
export const getEligibleEmployees = (
  asset: AssetExtended,
  allEmployees: { id: number; name: string; code: string | null; customerId: number }[]
) => {
  const assetCustomerEmployees = allEmployees.filter(emp => emp.customerId === asset.customerId)

  if (isAssetAssignedToEmployee(asset)) {
    // Only the employee currently using the asset
    const currentEmployee = assetCustomerEmployees.find(emp => emp.id === asset.costObjectId)
    return currentEmployee ? [currentEmployee] : []
  }

  if (isAssetAvailable(asset)) {
    // All employees in the company
    return assetCustomerEmployees.filter(emp => emp.id !== null)
  }

  return []
}

/**
 * Returns a user-friendly reason why an asset cannot be sold
 */
export const getIneligibilityReason = (asset: AssetExtended): string | undefined => {
  if (asset.isLeased) {
    return 'Asset is leased and cannot be sold'
  }

  if (INELIGIBLE_STATUSES.includes(asset.status)) {
    return `Asset status "${asset.status}" is not eligible for sale`
  }

  if (!ELIGIBLE_STATUSES.includes(asset.status)) {
    return 'Asset status is not eligible for sale'
  }

  return undefined
}
